import type { VerifiedCheckoutPurchase } from "../../../src/billing/checkout-purchase";

const MAX_AGE_MS = 60 * 60 * 1000;
const CHECKOUT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface TrackerOptions {
  storage: () => {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
  };
  storageKey: string;
  verify: (id: string) => Promise<{ purchase: VerifiedCheckoutPurchase | null }>;
  report: (purchase: VerifiedCheckoutPurchase) => void;
  now?: () => number;
  wait?: () => Promise<void>;
}

/** Owned by the app, not the receipt route. Storage lets a reload resume work. */
export function createPendingCheckoutTracker(options: TrackerOptions) {
  const now = options.now ?? Date.now;
  const wait = options.wait ?? (() => new Promise<void>((resolve) => setTimeout(resolve, 1500)));
  const pending = new Map<string, number>();
  const running = new Set<string>();
  let stopped = false;

  function persist() {
    try {
      const storage = options.storage();
      if (pending.size) storage.setItem(options.storageKey, JSON.stringify(Object.fromEntries(pending)));
      else storage.removeItem(options.storageKey);
    } catch { /* In-memory tracking still survives SPA navigation. */ }
  }

  async function verify(id: string) {
    if (stopped || running.has(id)) return;
    running.add(id);
    try {
      for (let attempt = 0; attempt < 20 && !stopped; attempt++) {
        const createdAt = pending.get(id);
        if (createdAt === undefined) return;
        if (now() - createdAt > MAX_AGE_MS) {
          pending.delete(id);
          persist();
          return;
        }
        try {
          const { purchase } = await options.verify(id);
          if (stopped) return;
          if (purchase) {
            options.report(purchase);
            pending.delete(id);
            persist();
            return;
          }
        } catch { /* Retry transient API or authentication failures. */ }
        if (attempt < 19 && !stopped) await wait();
      }
      // Keep unresolved checkouts for the next app mount, without endless polling.
    } finally {
      running.delete(id);
    }
  }

  return {
    start() {
      try {
        const saved: unknown = JSON.parse(options.storage().getItem(options.storageKey) ?? "{}");
        if (saved && typeof saved === "object" && !Array.isArray(saved)) {
          for (const [id, timestamp] of Object.entries(saved)) {
            if (CHECKOUT_ID.test(id) && typeof timestamp === "number" &&
                Number.isFinite(timestamp) && timestamp <= now() && now() - timestamp <= MAX_AGE_MS) {
              pending.set(id, timestamp);
            }
          }
        }
      } catch { /* Missing, blocked, or malformed storage is safe to ignore. */ }
      persist();
      for (const id of pending.keys()) void verify(id);
    },
    enqueue(id: string) {
      if (stopped || !CHECKOUT_ID.test(id)) return;
      if (!pending.has(id)) pending.set(id, now());
      persist();
      void verify(id);
    },
    stop() { stopped = true; },
  };
}
